// web/src/screens/admin/textsets_list.ts — Plan 11-07 Task 3 (ADMIN-07).
//
// Lists Named TextSets via GET /api/admin/textsets (the endpoint already
// filters set_type='named'). Anonymous TextSets are inline-edited by parent
// entities (Action, Task) and have no admin URL surface.
//
// "+ New TextSet" prompts for short_code + name, POSTs to /api/admin/textsets,
// and navigates to the new row's edit screen. Server enforces uniqueness via
// 23505 → 409 duplicate_short_code (Plan 11-04b).
//
// All API-supplied text via textContent (T-11-09a/b). NO raw-HTML setters.

import { renderAdminShell, showAdminToast } from "./admin_shell.js";
import { adminGet, adminPost, AdminError } from "../../api/admin.js";
import type { AdminTextSetDTO } from "../../types/api.js";

function navigateAdmin(path: string): void {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function mount(root: HTMLElement, _props?: unknown): () => void {
  const main = renderAdminShell(root, [{ label: "TextSets" }]);

  const toolbar = document.createElement("div");
  toolbar.setAttribute(
    "style",
    "display: flex; gap: 12px; align-items: center; margin-bottom: 16px;",
  );
  const newBtn = document.createElement("button");
  newBtn.type = "button";
  newBtn.textContent = "+ New TextSet";
  newBtn.setAttribute(
    "style",
    "padding: 8px 14px; background: #2563eb; color: white; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;",
  );
  toolbar.appendChild(newBtn);
  main.appendChild(toolbar);

  const status = document.createElement("p");
  status.textContent = "Loading…";
  status.setAttribute("style", "color: #6b7280; font-size: 14px;");
  main.appendChild(status);

  const listEl = document.createElement("ul");
  listEl.setAttribute(
    "style",
    "list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px;",
  );
  main.appendChild(listEl);

  function renderRow(set: AdminTextSetDTO): HTMLLIElement {
    const li = document.createElement("li");
    li.setAttribute(
      "style",
      "display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: white; border: 1px solid #e5e7eb; border-radius: 6px;",
    );
    const a = document.createElement("a");
    a.textContent = set.short_code ?? "(no short_code)"; // textContent
    const sc = set.short_code ?? "";
    a.setAttribute("href", `/admin/textsets/${encodeURIComponent(sc)}`);
    a.setAttribute(
      "style",
      "color: #1d4ed8; text-decoration: none; font-weight: 500; min-width: 200px;",
    );
    a.addEventListener("click", (e) => {
      e.preventDefault();
      if (!sc) return;
      navigateAdmin(`/admin/textsets/${encodeURIComponent(sc)}`);
    });
    li.appendChild(a);

    const nameSpan = document.createElement("span");
    nameSpan.textContent = set.name ?? "(unnamed)";
    nameSpan.setAttribute("style", "color: #374151; font-size: 14px;");
    li.appendChild(nameSpan);

    return li;
  }

  async function load(): Promise<void> {
    try {
      const sets = await adminGet<AdminTextSetDTO[]>("/api/admin/textsets");
      listEl.replaceChildren();
      if (sets.length === 0) {
        status.textContent = "No named TextSets yet — create one with the “+ New TextSet” button.";
        return;
      }
      status.textContent = `${sets.length} TextSet${sets.length === 1 ? "" : "s"}.`;
      for (const s of sets) listEl.appendChild(renderRow(s));
    } catch (err) {
      if (err instanceof AdminError && (err.status === 401 || err.status === 403)) {
        status.textContent = "Not signed in — visit /login to authenticate, then refresh.";
      } else {
        status.textContent = `Failed to load: ${(err as Error).message}`;
      }
    }
  }

  async function onNew(): Promise<void> {
    const sc = window.prompt("New TextSet short_code (lowercase, [a-z0-9_-]+):");
    if (!sc) return;
    const name = window.prompt("Display name:");
    if (name == null) return;
    try {
      const created = await adminPost<Partial<AdminTextSetDTO>, AdminTextSetDTO>(
        "/api/admin/textsets",
        { short_code: sc, name, set_type: "named" },
      );
      showAdminToast(document.body, `Created ${created.short_code}`);
      navigateAdmin(`/admin/textsets/${encodeURIComponent(created.short_code ?? sc)}`);
    } catch (err) {
      if (err instanceof AdminError && err.status === 409) {
        showAdminToast(document.body, "short_code already exists");
      } else if (err instanceof AdminError && err.status === 422) {
        showAdminToast(document.body, "Invalid input — check short_code regex + required name");
      } else {
        showAdminToast(document.body, `Create failed: ${(err as Error).message}`);
      }
    }
  }

  newBtn.addEventListener("click", onNew);

  void load();

  return () => {
    newBtn.removeEventListener("click", onNew);
  };
}
